// ./modules/foundher/FoundHerPDFProfile.jsx
import React, {useEffect} from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const FoundHerPDFProfile = ({ formData, authData = {}, onDownloadComplete }) => {
  const generatePDF = () => {
    if (!formData || window.pdfAlreadyGenerated) return; 
    
    window.pdfAlreadyGenerated = true;

    if (!formData) {
      console.error("❌ formData is missing");
      return;
    }

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Feminine pink theme
    doc.setFillColor(253, 242, 248);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');

    doc.setDrawColor(236, 72, 153);
    doc.setLineWidth(5);
    doc.rect(12, 12, pageWidth - 24, pageHeight - 24);

    doc.setDrawColor(249, 168, 212);
    doc.setLineWidth(1.5);
    doc.rect(18, 18, pageWidth - 36, pageHeight - 36);

    let y = 38;

    // Header
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(36);
    doc.setTextColor(190, 24, 93);
    doc.text("FOUNDHER", pageWidth / 2, y, { align: 'center' });

    y += 13;
    doc.setFontSize(13);
    doc.setTextColor(219, 39, 119);
    doc.text("Women Who Rise • Women Who Lead", pageWidth / 2, y, { align: 'center' });

    y += 24;

    // Name
    doc.setFontSize(27);
    doc.setTextColor(157, 23, 77);
    const name = (formData.userName || "Visionary Woman").toUpperCase();
    doc.text(name, pageWidth / 2, y, { align: 'center' });

    y += 14;

    if (formData.userCode) {
      doc.setFontSize(12);
      doc.setTextColor(107, 114, 128);
      doc.text(`Profile Code • ${formData.userCode}`, pageWidth / 2, y, {
        align: "center",
      });
      y += 18;
    }

    // ==================== ADDRESS + CONTACT BLOCK ====================
    doc.setFontSize(11.5);
    doc.setTextColor(75, 85, 99);

    const addressParts = [];

    // Main address from form
    if (formData.address) addressParts.push(formData.address);

    // ==================== ADDRESS & CONTACT DETAILS ====================
    const a = authData || {};

    doc.setFontSize(11.5);
    doc.setTextColor(75, 85, 99);

    // 1. Main Address
    if (formData.address) {
      doc.text(formData.address, pageWidth / 2, y, { align: "center" });
      y += 10;
    }

    // 2. Area, City
    const areaCity = [a.area, a.city].filter(Boolean).join(", ");
    if (areaCity) {
      doc.text(areaCity, pageWidth / 2, y, { align: "center" });
      y += 9;
    }

    // 3. State • Country • PIN
    const locationInfo = [
      a.state,
      a.country,
      a.pinCode ? `PIN: ${a.pinCode}` : null,
    ]
      .filter(Boolean)
      .join(" • ");

    if (locationInfo) {
      doc.text(locationInfo, pageWidth / 2, y, { align: "center" });
      y += 10;
    }

    // 4. Mobile & Email (Clean, no emojis)
    const contactParts = [];
    if (a.mobile) contactParts.push(`Mobile: ${a.mobile}`);
    if (a.email) contactParts.push(`Email: ${a.email}`);

    if (contactParts.length > 0) {
      doc.setFontSize(11);
      doc.setTextColor(190, 24, 93);
      doc.text(contactParts.join("   |   "), pageWidth / 2, y, {
        align: "center",
      });
      y += 22;
    } else {
      y += 15;
    }

    // Rest of the sections (same beautiful design)
    const sections = [
      { title: "My Journey So Far", content: formData.journeySoFar },
      { title: "Dreams & Aspirations", content: formData.dreams },
      { title: "My Purpose", content: formData.purpose },
      { title: "Challenges Overcome", content: formData.challenges },
    ];

    sections.forEach(({ title, content }) => {
      if (y > pageHeight - 85) {
        doc.addPage();
        y = 45;
      }

      doc.setDrawColor(236, 72, 153);
      doc.setLineWidth(1.5);
      doc.line(25, y - 5, 60, y - 5);

      doc.setFontSize(16);
      doc.setTextColor(190, 24, 93);
      doc.setFont('helvetica', 'bold');
      doc.text(title, 25, y);
      y += 11;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.setTextColor(55, 65, 81);
      
      const textLines = doc.splitTextToSize(
        content || "Crafting her legacy with courage and vision...", 
        pageWidth - 50
      );
      doc.text(textLines, 25, y);
      y += textLines.length * 6.8 + 24;
    });

    // Socials
    const socials = Object.entries(formData.socialProfiles || {})
      .filter(([_, v]) => v?.trim());

    if (socials.length > 0) {
      if (y > pageHeight - 90) {
        doc.addPage();
        y = 45;
      }

      doc.setFontSize(16);
      doc.setTextColor(190, 24, 93);
      doc.setFont('helvetica', 'bold');
      doc.text("Connect With Me", 25, y);
      y += 14;

      doc.setFontSize(11.5);
      socials.forEach(([platform, url]) => {
        doc.setTextColor(75, 85, 99);
        doc.text(`${platform.charAt(0).toUpperCase() + platform.slice(1)}:`, 28, y);
        doc.setTextColor(236, 72, 153);
        doc.textWithLink(url.replace(/^https?:\/\//, ''), 72, y, { url });
        y += 10;
      });
    }

    // Footer
    const footerY = pageHeight - 26;
    doc.setDrawColor(249, 168, 212);
    doc.line(28, footerY - 10, pageWidth - 28, footerY - 10);

    doc.setFontSize(10);
    doc.setTextColor(124, 58, 237);
    doc.text("FoundHer — Celebrating Women Founders", pageWidth / 2, footerY, { align: 'center' });
    
    doc.setFontSize(9.5);
    doc.setTextColor(107, 114, 128);
    doc.text(
      `Generated on ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}`, 
      pageWidth / 2, footerY + 7, { align: 'center' }
    );

    const safeName = (formData.userName || 'Woman_Founder').replace(/[^a-zA-Z0-9]/g, '_');
    setTimeout(() => {
      window.pdfAlreadyGenerated = false;
      if (onDownloadComplete) onDownloadComplete();
    }, 800);

    doc.save(`${safeName}_FoundHer_Profile.pdf`);

    if (onDownloadComplete) onDownloadComplete();
  };

  useEffect(() => {
    generatePDF();
  }, []);

  return null;
};

export default FoundHerPDFProfile;